import { styled } from '@/src/stitches.config'
import { ADD_CARD, REMOVE_CARD } from '@/src/modules/build-a-card/constants'
import {
    TRecipientInfo,
    TRecipientPlasticInfo,
    TRecipientPlasticState,
    TRecipientState,
} from '@/src/modules/build-a-card/inputs/types'
import { TPartnerPayload } from '@/src/modules/partners/types'
import { HeaderParagraphText } from '@/src/modules/build-a-card/StyledComponents'
import { SimpleFieldWrapper } from '@/src/modules/build-a-card/StepElement'

const QuantityButtonsWrapper = styled('div', {
    display: 'flex',
    width: 'fit-content',
    color: '$darkest',
    '@sm': {
        maxWidth: '100%',
    },
})

const baseInputStyles = {
    height: 60,
    fontSize: 22,
    textAlign: 'center',
    border: 'none',
    marginTop: '0px !important',
    marginBottom: '0px !important',
    backgroundColor: '$white',
    py: 15,
    variants: {
        scheme: {
            error: {
                border: '2px solid $error',
            },
        },
    },
}

const Quantity = styled('p', {
    ...baseInputStyles,
    borderTop: '2px solid $darkest',
    borderBottom: '2px solid $darkest',
    width: 80,
    fontSize: '22px !important',
    fontWeight: 'bold',
    boxSizing: 'border-box',
    '&:focus, :active': {
        outline: 'none',
    },
})

const IncrementDecrementButtons = styled('button', {
    ...baseInputStyles,
    cursor: 'pointer',
    width: 55,
    color: '$primary',
    border: '2px solid $darkest',
    '&:hover, &:focus': {
        backgroundColor: '$primaryLight',
        outlineColor: '$darkest',
    },
    '&:active': {
        backgroundColor: '$primary',
        color: '$white',
    },
    variants: {
        borderRadius: {
            default: {},
            left: {
                borderTopLeftRadius: 5,
                borderBottomLeftRadius: 5,
            },
            right: {
                borderTopRightRadius: 5,
                borderBottomRightRadius: 5,
            },
        },
    },
    defaultVariants: {
        borderRadius: 'default',
    },
})

const ContentWrapper = styled('div', {
    variants: {
        wrap: {
            true: { display: 'flex', gap: 15, alignItems: 'center' },
        },
    },
})

const BulkLink = styled('a', {
    color: '$secondary',
    textDecoration: 'underline',
    '&:hover': {
        color: '$secondaryDark',
    },
})

const BulkLinkWrapper = styled('div', {
    gap: 5,
    margin: 'auto',
    lineHeight: 1.5,
    fontSize: 14,
    variants: {
        wrap: {
            false: {
                display: 'flex',
                flexWrap: 'wrap',
                marginTop: 10,
            },
        },
    },
})

interface IQuantitySelect {
    giftState: TRecipientState | TRecipientPlasticState
    dispatch: any
    ctaUrl?: string
    defaultRecipient?: TRecipientInfo | TRecipientPlasticInfo
    label?: string
    wrap?: boolean
    maxWidth?: number | string
    partnerData?: TPartnerPayload
}

const QuantitySelectorButton = ({
    giftState,
    dispatch,
    ctaUrl,
    defaultRecipient = undefined,
    label = 'Choose the quantity:',
    wrap = false,
    maxWidth = '100%',
    partnerData,
}: IQuantitySelect) => {
    const { cardQuantity, minValue, maxValue } = giftState

    return (
        <SimpleFieldWrapper>
            <HeaderParagraphText>{label}</HeaderParagraphText>
            <ContentWrapper wrap={wrap}>
                <QuantityButtonsWrapper style={{ maxWidth: maxWidth }}>
                    <IncrementDecrementButtons
                        onClick={(e) => {
                            e.preventDefault()
                            dispatch({
                                type: REMOVE_CARD,
                                payload: { defaultRecipient: defaultRecipient },
                            })
                        }}
                        disabled={cardQuantity === minValue}
                        type={'button'}
                        borderRadius={'left'}
                    >
                        &ndash;
                    </IncrementDecrementButtons>
                    <Quantity>{cardQuantity}</Quantity>
                    <IncrementDecrementButtons
                        onClick={(e) => {
                            e.preventDefault()
                            dispatch({
                                type: ADD_CARD,
                                payload: { defaultRecipient: defaultRecipient },
                            })
                        }}
                        disabled={cardQuantity === maxValue}
                        type={'button'}
                        borderRadius={'right'}
                    >
                        &#43;
                    </IncrementDecrementButtons>
                </QuantityButtonsWrapper>
                {!partnerData && (
                    <BulkLinkWrapper wrap={wrap}>
                        Interested in buying a larger quantity?{' '}
                        <BulkLink
                            href={ctaUrl ?? '/corporate/get-started'}
                            target="_blank"
                        >
                            Buy in bulk
                        </BulkLink>
                    </BulkLinkWrapper>
                )}
            </ContentWrapper>
        </SimpleFieldWrapper>
    )
}

export default QuantitySelectorButton
